// Settings-host search: match properties by title/description. Grouped area →
// section → fields. Only fields whose label or description contain the query
// are returned — a matching area or section title does not pull in siblings.

export const SETTINGS_SEARCH_DEBOUNCE_MS = 180;

export interface SettingsSearchSection<TField extends SettingsSearchField> {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly fields: readonly TField[];
}

export interface SettingsSearchArea<TField extends SettingsSearchField> {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly sections: readonly SettingsSearchSection<TField>[];
}

export interface SettingsSearchField {
  readonly label: string;
  readonly description: string;
}

export interface SettingsSearchHit<TField extends SettingsSearchField> {
  readonly path: string;
  readonly title: string;
  readonly sections: readonly SettingsSearchSection<TField>[];
}

export function matchSettingsSearch<TField extends SettingsSearchField>(query: string, areas: readonly SettingsSearchArea<TField>[]): readonly SettingsSearchHit<TField>[] {

  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [];

  const hits: SettingsSearchHit<TField>[] = [];
  for (const area of areas) {
    const sections: SettingsSearchSection<TField>[] = [];
    for (const section of area.sections) {
      const fields = section.fields.filter(field => textMatches(field.label, needle) || textMatches(field.description, needle));
      if (fields.length === 0) continue;
      sections.push({ ...section, fields });
    }
    if (sections.length === 0) continue;
    hits.push({ path: area.path, title: area.title, sections });
  }
  return hits;
}

function textMatches(value: string, needle: string): boolean {

  return value.toLocaleLowerCase().includes(needle);
}

export function highlightSettingsSearch(text: string, query: string): string {

  const source = text ?? '';
  const needle = query.trim();
  if (!needle) return escapeHtml(source);

  const ranges = hitRanges(source.toLocaleLowerCase(), needle.toLocaleLowerCase());
  if (ranges.length === 0) return escapeHtml(source);

  const parts: string[] = [];
  let from = 0;
  for (const range of ranges) {
    parts.push(escapeHtml(source.slice(from, range.start)));
    parts.push(`<mark>${escapeHtml(source.slice(range.start, range.end))}</mark>`);
    from = range.end;
  }
  parts.push(escapeHtml(source.slice(from)));
  return parts.join('');
}

function hitRanges(lower: string, needleLower: string): readonly { start: number; end: number }[] {

  const ranges: { start: number; end: number }[] = [];
  const needleLength = needleLower.length;
  let index = lower.indexOf(needleLower);
  while (index !== -1) {
    const end = index + needleLength;
    ranges.push({ start: index, end });
    index = lower.indexOf(needleLower, end);
  }
  return mergeRanges(ranges);
}

function mergeRanges(ranges: readonly { start: number; end: number }[]): readonly { start: number; end: number }[] {

  if (ranges.length === 0) return ranges;
  const ordered = [...ranges].sort((left, right) => left.start - right.start);
  const merged = [{ ...ordered[0] }];
  for (let i = 1; i < ordered.length; i++) {
    const current = merged[merged.length - 1];
    const next = ordered[i];
    if (next.start <= current.end) current.end = Math.max(current.end, next.end);
    else merged.push({ ...next });
  }
  return merged;
}

function escapeHtml(value: string): string {

  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}
